// Single source of truth for "what's the user's karma balance right now",
// shared by every surface that renders one.
//
// Before this hook, each surface fetched independently and only WalletPage
// listened for `karma:refresh` — so a payment anywhere else left the header,
// dashboards, and cards showing a stale number until a full page reload.
// Consumers get the hook's `status` ('loading' | 'known' | 'unauthenticated'
// | 'error') instead of a bare number so a failed/expired-auth read is never
// silently rendered as "you have 0 karma" (see utils/wallet.js).
import { useCallback, useEffect, useState } from 'react';
import { fetchKarmaWallet } from '../utils/wallet';

/**
 * @param {string|undefined|null} userId
 * @returns {{status: 'loading'|'known'|'unauthenticated'|'error', balance: number|null, refresh: () => void}}
 */
export function useKarmaBalance(userId) {
  const [wallet, setWallet] = useState({ status: 'loading', balance: null });

  const refresh = useCallback(async () => {
    if (!userId) {
      setWallet({ status: 'unauthenticated', balance: null });
      return;
    }
    setWallet((w) => (w.status === 'known' ? w : { status: 'loading', balance: null }));
    setWallet(await fetchKarmaWallet(userId));
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    window.addEventListener('karma:refresh', refresh);
    return () => window.removeEventListener('karma:refresh', refresh);
  }, [refresh]);

  return { ...wallet, refresh };
}

export default useKarmaBalance;
